import { FC, KeyboardEvent, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";

interface BlockDraftProps {
  initialDescription?: string;
  onSubmit: (description: string) => void;
  onCancel: () => void;
}

export const BlockDraft: FC<BlockDraftProps> = ({
  initialDescription = "",
  onSubmit,
  onCancel,
}) => {
  const initialPending = initialDescription.trim().length > 0;
  const [description, setDescription] = useState(initialDescription);
  const [pending, setPending] = useState(initialPending);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setDescription(initialDescription);
    setPending(initialDescription.trim().length > 0);
  }, [initialDescription]);

  useEffect(() => {
    if (!pending) {
      inputRef.current?.focus();
    }
  }, [pending]);

  const submit = () => {
    const trimmed = description.trim();
    if (!trimmed || pending) return;
    setPending(true);
    onSubmit(trimmed);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <Card $pending={pending}>
      <Header>
        <PulseDot />
        <MDSText
          variant={MDSTextVariant.TextXs}
          weight={MDSTextWeight.SemiBold}
          color={MDSTextColor.Tertiary}
        >
          {pending ? "MEM IS COMPOSING…" : "NEW BLOCK"}
        </MDSText>
      </Header>
      <Body>
        <DescriptionInput
          ref={inputRef}
          value={description}
          placeholder="Describe the block you want to add — Mem picks the type and content."
          readOnly={pending}
          onChange={e => setDescription(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
        />
        <Actions>
          {pending ? (
            <ShimmerStrip />
          ) : (
            <>
              <CancelButton type="button" onClick={onCancel}>
                Cancel
              </CancelButton>
              <SaveButton type="button" onClick={submit} disabled={!description.trim()}>
                Save
              </SaveButton>
            </>
          )}
        </Actions>
      </Body>
    </Card>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(2px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(0.85); }
  50% { opacity: 1; transform: scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const Card = styled.div<{ $pending: boolean }>`
  border: 1px dashed ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing["4"]};
  background: ${({ theme }) => theme.colors.surface.pane};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
  animation: ${fadeIn} 220ms ease-out;
  transition: opacity 0.2s ease;
  opacity: ${({ $pending }) => ($pending ? 0.85 : 1)};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PulseDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ff6b4a;
  animation: ${pulse} 0.9s ease-in-out infinite;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const DescriptionInput = styled.textarea`
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: inherit;
  resize: none;
  outline: none;
  padding: 0;
  width: 100%;
  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
  &:read-only {
    cursor: default;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing["2"]};
  height: 28px;
`;

const CancelButton = styled.button`
  background: transparent;
  border: none;
  font-family: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const SaveButton = styled.button`
  border: none;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.surface.pane};
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ShimmerStrip = styled.div`
  flex: 1;
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.border.default}55 0%,
    ${({ theme }) => theme.colors.border.default}aa 50%,
    ${({ theme }) => theme.colors.border.default}55 100%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s linear infinite;
`;
